import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ShieldCheck, LogOut, Search, Package, Users, Calendar,
  TrendingUp, Loader2, Trash2, AlertTriangle, Zap, Edit3, Plus, Radio, DollarSign, Activity, Send, Mail, Satellite, Bot, KeyRound,
} from 'lucide-react';
import { adminApi, clearAdminToken } from '../../services/adminApi';
import AdminCreateTenantModal from '../../components/admin/AdminCreateTenantModal';

/**
 * Super-admin home — tenants directory (MVP scope chosen by product:
 * option (a) from the planning step: view all tenants).
 */
const AdminTenantsPage = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [atRiskOnly, setAtRiskOnly] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // tenant pending removal
  const [deleting, setDeleting] = useState(false);
  const [editPlan, setEditPlan] = useState(null); // tenant being edited
  const [savingPlan, setSavingPlan] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [meRes, tRes] = await Promise.all([adminApi.me(), adminApi.tenants()]);
        setMe(meRes.data);
        setTenants(tRes.data.tenants || []);
      } catch (err) {
        if (err.response?.status === 401) {
          clearAdminToken();
          navigate('/admin/login', { replace: true });
        } else {
          toast.error('Failed to load tenants');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  const handleLogout = () => {
    clearAdminToken();
    navigate('/admin/login', { replace: true });
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await adminApi.deleteTenant(confirmDelete.id);
      setTenants((ts) => ts.filter((t) => t.id !== confirmDelete.id));
      toast.success(`Removed ${confirmDelete.client_id} — ${confirmDelete.name}`);
      setConfirmDelete(null);
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Failed to remove tenant');
    } finally {
      setDeleting(false);
    }
  };

  /** One-click upgrade Free → Professional. Sends a welcome-to-Pro email. */
  const handleQuickUpgrade = async (tenant) => {
    if (!window.confirm(`Upgrade ${tenant.name} (${tenant.client_id}) to Professional? They'll get a confirmation email.`)) return;
    try {
      const { data } = await adminApi.updatePlan(tenant.id, { plan: 'professional' });
      setTenants((ts) => ts.map((t) => (t.id === tenant.id ? { ...t, plan: 'professional' } : t)));
      toast.success(`${tenant.client_id} upgraded to Professional — email sent.`);
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Upgrade failed.');
    }
  };

  /** Save plan + seats from the edit modal. */
  const handleSavePlan = async ({ plan, seats, gpsSeats }) => {
    if (!editPlan) return;
    setSavingPlan(true);
    try {
      const body = {};
      if (plan !== editPlan.plan) body.plan = plan;
      const seatsNum = seats === '' || seats === null ? null : Number(seats);
      if (seatsNum !== (editPlan.office_user_seats ?? null)) body.office_user_seats = seatsNum;
      const gpsNum = gpsSeats === '' || gpsSeats === null ? 0 : Number(gpsSeats);
      if (gpsNum !== (editPlan.gps_seats_included ?? 0)) body.gps_seats_included = gpsNum;
      if (!Object.keys(body).length) {
        toast.info('No changes to save');
        setEditPlan(null);
        setSavingPlan(false);
        return;
      }
      const { data } = await adminApi.updatePlan(editPlan.id, body);
      setTenants((ts) => ts.map((t) => (t.id === editPlan.id
        ? {
            ...t,
            plan: body.plan ?? t.plan,
            office_user_seats: body.office_user_seats !== undefined ? body.office_user_seats : t.office_user_seats,
            gps_seats_included: body.gps_seats_included !== undefined ? body.gps_seats_included : t.gps_seats_included,
            gps_billable_count: body.gps_seats_included !== undefined
              ? Math.max(0, (t.gps_count ?? 0) - body.gps_seats_included)
              : t.gps_billable_count,
          }
        : t)));
      toast.success(`${editPlan.client_id} updated — email sent to tenant.`);
      setEditPlan(null);
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Update failed.');
    } finally {
      setSavingPlan(false);
    }
  };

  // Cutoff captured once at mount — keeps render() pure and avoids the
  // react-hooks/purity rule firing on Date.now() inside the filter.
  const [atRiskCutoffMs] = useState(() => Date.now() - 7 * 86400000);

  const atRiskByTenantId = useMemo(() => {
    const out = {};
    for (const t of tenants) {
      const score = t.activation_score ?? 0;
      const created = t.created_at ? Date.parse(t.created_at) : NaN;
      out[t.id] = score < 3 && Number.isFinite(created) && created <= atRiskCutoffMs;
    }
    return out;
  }, [tenants, atRiskCutoffMs]);

  const filtered = tenants.filter((t) => {
    if (planFilter !== 'all' && t.plan !== planFilter) return false;
    if (atRiskOnly && !atRiskByTenantId[t.id]) return false;
    if (search) {
      const q = search.toLowerCase();
      return (t.name || '').toLowerCase().includes(q) ||
             (t.client_id || '').toLowerCase().includes(q) ||
             (t.country || '').toLowerCase().includes(q) ||
             (t.admin_email || '').toLowerCase().includes(q) ||
             (t.admin_name || '').toLowerCase().includes(q);
    }
    return true;
  });

  const atRiskCount = tenants.filter((t) => atRiskByTenantId[t.id]).length;

  const stats = {
    total: tenants.length,
    free: tenants.filter((t) => t.plan === 'free').length,
    pro: tenants.filter((t) => t.plan === 'professional').length,
    testDrive: tenants.filter((t) => t.source === 'test_drive').length,
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0e0e0e]" data-testid="admin-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#FFD108]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0e0e0e] text-white" data-testid="admin-tenants-page">

      {/* Top bar */}
      <header className="border-b border-white/5 bg-[#121212]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#FFD108] flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-black" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#FFD108] font-semibold">
                Super Admin
              </p>
              <h1 className="text-base font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                EQUIPR Guardian Console
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/billing')}
              className="h-9 px-3 rounded-lg bg-white/5 hover:bg-white/10 text-sm flex items-center gap-2 transition-colors"
              data-testid="admin-nav-billing"
            >
              <DollarSign className="w-4 h-4" /> Billing
            </button>
            <button
              onClick={() => navigate('/admin/audit')}
              className="h-9 px-3 rounded-lg bg-white/5 hover:bg-white/10 text-sm flex items-center gap-2 transition-colors"
              data-testid="admin-nav-audit"
            >
              <Activity className="w-4 h-4" /> Audit
            </button>
            <button
              onClick={() => navigate('/admin/broadcast')}
              className="h-9 px-3 rounded-lg bg-white/5 hover:bg-white/10 text-sm flex items-center gap-2 transition-colors"
              data-testid="admin-nav-broadcast"
            >
              <Send className="w-4 h-4" /> Broadcast
            </button>
            <button
              onClick={() => navigate('/admin/lifecycle')}
              className="h-9 px-3 rounded-lg bg-white/5 hover:bg-white/10 text-sm flex items-center gap-2 transition-colors"
              data-testid="admin-nav-lifecycle"
            >
              <Mail className="w-4 h-4" /> Lifecycle
            </button>
            <button
              onClick={() => navigate('/admin/telematics')}
              className="h-9 px-3 rounded-lg bg-white/5 hover:bg-white/10 text-sm flex items-center gap-2 transition-colors"
              data-testid="admin-nav-telematics"
            >
              <Radio className="w-4 h-4" /> Telematics
            </button>
            <button
              onClick={() => navigate('/admin/vdaq-health')}
              className="h-9 px-3 rounded-lg bg-white/5 hover:bg-white/10 text-sm flex items-center gap-2 transition-colors"
              data-testid="admin-nav-vdaq-health"
            >
              <Satellite className="w-4 h-4" /> V-DAQ
            </button>
            <button
              onClick={() => navigate('/admin/ai-logs')}
              className="h-9 px-3 rounded-lg bg-white/5 hover:bg-white/10 text-sm flex items-center gap-2 transition-colors"
              data-testid="admin-nav-ai-logs"
            >
              <Bot className="w-4 h-4" /> AI log
            </button>
            <button
              onClick={() => navigate('/admin/ai-keys')}
              className="h-9 px-3 rounded-lg bg-white/5 hover:bg-white/10 text-sm flex items-center gap-2 transition-colors"
              data-testid="admin-nav-ai-keys"
            >
              <KeyRound className="w-4 h-4" /> AI keys
            </button>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Signed in as</p>
              <p className="text-sm font-medium" data-testid="admin-current-email">{me?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="h-9 px-3 rounded-lg bg-white/5 hover:bg-white/10 text-sm flex items-center gap-2 transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Summary stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Users} label="Total tenants" value={stats.total} testid="stat-total" />
          <StatCard icon={TrendingUp} label="Free plan" value={stats.free} accent testid="stat-free" />
          <StatCard icon={Package} label="Professional" value={stats.pro} testid="stat-pro" />
          <StatCard icon={Calendar} label="From Test Drive" value={stats.testDrive} testid="stat-testdrive" />
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, Client ID, or country…"
              className="w-full h-10 pl-9 pr-3 rounded-lg bg-[#1a1a1a] border border-white/5 text-sm focus:outline-none focus:border-[#FFD108]/40"
              data-testid="admin-search-input"
            />
          </div>
          <div className="flex items-center gap-1 bg-[#1a1a1a] rounded-lg p-1 border border-white/5">
            {[
              { v: 'all', l: 'All' },
              { v: 'free', l: 'Free' },
              { v: 'professional', l: 'Pro' },
            ].map((opt) => (
              <button
                key={opt.v}
                onClick={() => setPlanFilter(opt.v)}
                className={`px-3 h-8 rounded-md text-xs font-semibold uppercase tracking-wider transition-colors ${
                  planFilter === opt.v
                    ? 'bg-[#FFD108] text-black'
                    : 'text-muted-foreground hover:text-white'
                }`}
                data-testid={`admin-plan-filter-${opt.v}`}
              >
                {opt.l}
              </button>
            ))}
          </div>
          <button
            onClick={() => setAtRiskOnly((v) => !v)}
            title="Activation score below 3 and signed up over 7 days ago — the tenants worth calling this week."
            className={`inline-flex items-center gap-2 h-10 px-3 rounded-lg text-xs font-semibold uppercase tracking-wider border transition-colors ${
              atRiskOnly
                ? 'bg-red-500/15 text-red-300 border-red-500/40'
                : 'bg-[#1a1a1a] text-muted-foreground border-white/5 hover:text-white'
            }`}
            data-testid="admin-at-risk-filter"
          >
            <AlertTriangle className={`w-3.5 h-3.5 ${atRiskOnly ? 'text-red-300' : ''}`} />
            At risk
            <span className={`ml-1 tabular-nums px-1.5 rounded ${atRiskOnly ? 'bg-red-500/30' : 'bg-white/5'}`}>
              {atRiskCount}
            </span>
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#FFD108] text-black text-sm font-semibold uppercase tracking-wider hover:bg-[#ffdf3a] transition-colors"
            data-testid="admin-create-tenant-btn"
          >
            <Plus className="w-4 h-4" />
            Create tenant
          </button>
        </div>

        {/* Tenants table */}
        <div className="rounded-xl border border-white/5 overflow-hidden bg-[#121212]">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-tenants-table">
              <thead className="bg-[#1a1a1a] text-muted-foreground text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Client ID</th>
                  <th className="text-left px-4 py-3 font-semibold">Company</th>
                  <th className="text-left px-4 py-3 font-semibold">Plan</th>
                  <th className="text-left px-4 py-3 font-semibold">Source</th>
                  <th className="text-right px-4 py-3 font-semibold">Assets</th>
                  <th className="text-right px-4 py-3 font-semibold">Users</th>
                  <th className="text-right px-4 py-3 font-semibold">Seats</th>
                  <th className="text-right px-4 py-3 font-semibold" title="GPS devices currently bound · free allowance · billable">GPS</th>
                  <th className="text-left px-4 py-3 font-semibold" title="Activation milestones: signup · asset · invite · inspection · geofence">Activation</th>
                  <th className="text-left px-4 py-3 font-semibold">Country</th>
                  <th className="text-left px-4 py-3 font-semibold">Last active</th>
                  <th className="text-left px-4 py-3 font-semibold">Signed up</th>
                  <th className="px-4 py-3 font-semibold w-32 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={13} className="text-center text-muted-foreground py-12" data-testid="admin-empty">
                      No tenants match your filters.
                    </td>
                  </tr>
                )}
                {filtered.map((t) => (
                  <tr
                    key={t.id}
                    className="border-t border-white/5 hover:bg-white/[0.02] transition-colors"
                    data-testid={`tenant-row-${t.client_id}`}
                  >
                    <td className="px-4 py-3 font-mono text-xs text-[#FFD108]">{t.client_id}</td>
                    <td className="px-4 py-3" data-testid={`tenant-name-${t.client_id}`}>
                      <div className="font-medium">{t.name}</div>
                      {(t.admin_email || t.admin_name) && (
                        <div className="text-[11px] text-muted-foreground mt-0.5 font-mono">
                          {t.admin_name ? `${t.admin_name} · ` : ''}
                          {t.admin_email && (
                            <a
                              href={`mailto:${t.admin_email}`}
                              className="hover:text-[#FFD108]"
                              data-testid={`tenant-admin-email-${t.client_id}`}
                            >
                              {t.admin_email}
                            </a>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <PlanBadge plan={t.plan} />
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {t.source === 'test_drive' ? 'Test Drive' : t.source === 'super_admin_create' ? 'Provisioned' : 'Register'}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{t.asset_count}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{t.user_count}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {t.plan === 'professional' ? (t.office_user_seats ?? <span className="text-muted-foreground">—</span>) : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-xs" data-testid={`tenant-gps-${t.client_id}`}>
                      {t.plan === 'professional' ? (
                        <span title={`${t.gps_count ?? 0} bound · ${t.gps_seats_included ?? 0} free · ${t.gps_billable_count ?? 0} billable`}>
                          <span className="text-white">{t.gps_count ?? 0}</span>
                          {(t.gps_seats_included ?? 0) > 0 && (
                            <span className="text-[#FFD108]">/{t.gps_seats_included}</span>
                          )}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3" data-testid={`tenant-activation-${t.client_id}`}>
                      <ActivationCell tenant={t} />
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{t.country || '—'}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{formatDate(t.last_active_at)}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{formatDate(t.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {t.plan === 'free' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleQuickUpgrade(t); }}
                            className="h-7 px-2 rounded-md inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider bg-[#FFD108] text-black hover:bg-[#E5B93D] transition-colors"
                            title="One-click upgrade to Professional"
                            data-testid={`tenant-upgrade-btn-${t.client_id}`}
                          >
                            <Zap className="w-3 h-3" />
                            Upgrade
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditPlan(t); }}
                          className="w-8 h-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-[#FFD108] hover:bg-[#FFD108]/10 transition-colors"
                          title="Edit plan & seats"
                          data-testid={`tenant-edit-plan-btn-${t.client_id}`}
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setConfirmDelete(t); }}
                          className="w-8 h-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors"
                          title="Remove tenant"
                          data-testid={`tenant-delete-btn-${t.client_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Edit plan + seats modal */}
      {editPlan && (
        <EditPlanModal
          tenant={editPlan}
          saving={savingPlan}
          onCancel={() => setEditPlan(null)}
          onSave={handleSavePlan}
        />
      )}

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !deleting && setConfirmDelete(null)}
          data-testid="admin-delete-modal"
        >
          <div
            className="bg-[#1a1a1a] border border-red-500/30 rounded-xl max-w-md w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-red-500/15 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h3 className="text-base font-bold">Remove tenant?</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  This will permanently delete{' '}
                  <span className="text-white font-semibold">{confirmDelete.name}</span>{' '}
                  (<span className="text-[#FFD108] font-mono text-xs">{confirmDelete.client_id}</span>)
                  and all of its data — assets, work orders, inspections, certificates, users.
                  <br />
                  <span className="text-red-400 font-semibold">This cannot be undone.</span>
                </p>
              </div>
            </div>
            <div className="bg-[#0e0e0e] rounded-lg p-3 mb-5 text-xs text-muted-foreground border border-white/5">
              <div className="flex justify-between py-1">
                <span>Assets</span>
                <span className="text-white tabular-nums">{confirmDelete.asset_count}</span>
              </div>
              <div className="flex justify-between py-1">
                <span>Users</span>
                <span className="text-white tabular-nums">{confirmDelete.user_count}</span>
              </div>
              <div className="flex justify-between py-1">
                <span>Plan</span>
                <span className="text-white">{confirmDelete.plan}</span>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirmDelete(null)}
                disabled={deleting}
                className="h-10 px-4 rounded-lg bg-white/5 hover:bg-white/10 text-sm font-medium disabled:opacity-50 transition-colors"
                data-testid="admin-delete-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="h-10 px-4 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-semibold uppercase tracking-wide disabled:opacity-50 flex items-center gap-2 transition-colors"
                data-testid="admin-delete-confirm-btn"
              >
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                {deleting ? 'Removing…' : 'Remove tenant'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreate && (
        <AdminCreateTenantModal
          onClose={() => setShowCreate(false)}
          onCreated={(newTenant) => {
            // Optimistically prepend; the modal keeps itself open to show the
            // SuccessCard with the auto-generated initial_password. The user
            // dismisses via the "Done" button which calls onClose.
            setTenants((ts) => [newTenant, ...ts]);
          }}
        />
      )}
    </div>
  );
};

const EditPlanModal = ({ tenant, saving, onCancel, onSave }) => {
  const [plan, setPlan] = useState(tenant.plan || 'free');
  const [seats, setSeats] = useState(tenant.office_user_seats ?? '');
  const [gpsSeats, setGpsSeats] = useState(tenant.gps_seats_included ?? 0);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => !saving && onCancel()}
      data-testid="admin-edit-plan-modal"
    >
      <div
        className="bg-[#1a1a1a] border border-[#FFD108]/30 rounded-xl max-w-md w-full p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-[#FFD108]/15 flex items-center justify-center shrink-0">
            <Edit3 className="w-5 h-5 text-[#FFD108]" />
          </div>
          <div>
            <h3 className="text-base font-bold">Edit plan & seats</h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              <span className="text-white">{tenant.name}</span>{' '}
              (<span className="text-[#FFD108] font-mono text-xs">{tenant.client_id}</span>) ·
              <span className="ml-1">an email will be sent to the tenant admin</span>
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">
              Plan
            </label>
            <div className="grid grid-cols-2 gap-2">
              {['free', 'professional'].map((p) => (
                <button
                  key={p}
                  onClick={() => setPlan(p)}
                  className={`h-11 rounded-lg border text-sm font-semibold uppercase tracking-wide transition-colors ${
                    plan === p
                      ? 'border-[#FFD108] bg-[#FFD108]/10 text-[#FFD108]'
                      : 'border-white/10 text-muted-foreground hover:border-white/20'
                  }`}
                  data-testid={`edit-plan-${p}-btn`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">
              Office user seats {plan === 'free' && <span className="text-muted-foreground/60 normal-case font-normal">(Pro only)</span>}
            </label>
            <input
              type="number"
              min="0"
              value={seats}
              onChange={(e) => setSeats(e.target.value)}
              disabled={plan === 'free'}
              placeholder={plan === 'free' ? '—' : 'e.g. 5'}
              className="w-full h-11 px-3 rounded-lg bg-[#262626] border border-white/5 text-white focus:outline-none focus:border-[#FFD108]/40 disabled:opacity-40"
              data-testid="edit-plan-seats-input"
            />
            <p className="text-[11px] text-muted-foreground mt-1.5">
              Number of paid Office user seats the tenant is allowed. Field & mobile users are always free.
            </p>
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">
              GPS Integration seats {plan === 'free' && <span className="text-muted-foreground/60 normal-case font-normal">(Pro only)</span>}
            </label>
            <input
              type="number"
              min="0"
              value={gpsSeats}
              onChange={(e) => setGpsSeats(e.target.value)}
              disabled={plan === 'free'}
              placeholder={plan === 'free' ? '—' : 'e.g. 5'}
              className="w-full h-11 px-3 rounded-lg bg-[#262626] border border-white/5 text-white focus:outline-none focus:border-[#FFD108]/40 disabled:opacity-40"
              data-testid="edit-plan-gps-seats-input"
            />
            <div className="mt-2 p-3 rounded-lg bg-black/30 border border-white/5 text-[11px] space-y-1">
              <div className="flex justify-between text-muted-foreground">
                <span>Currently bound:</span>
                <span className="font-mono text-white" data-testid="edit-plan-gps-current">
                  {tenant.gps_count ?? 0} device{(tenant.gps_count ?? 0) === 1 ? '' : 's'}
                </span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Billable after this change:</span>
                <span className="font-mono text-[#FFD108]" data-testid="edit-plan-gps-billable">
                  {Math.max(0, (tenant.gps_count ?? 0) - (parseInt(gpsSeats, 10) || 0))} × $1.99/mo
                </span>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1.5">
              First N GPS devices are <strong>free</strong> for this tenant (your comp allowance). Anything beyond is billed at $1.99/asset/month via Stripe.
            </p>
          </div>
        </div>

        <div className="flex gap-2 justify-end mt-6">
          <button
            onClick={onCancel}
            disabled={saving}
            className="h-10 px-4 rounded-lg bg-white/5 hover:bg-white/10 text-sm font-medium disabled:opacity-50 transition-colors"
            data-testid="edit-plan-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave({ plan, seats, gpsSeats })}
            disabled={saving}
            className="h-10 px-4 rounded-lg bg-[#FFD108] hover:bg-[#E5B93D] text-black text-sm font-semibold uppercase tracking-wide disabled:opacity-50 flex items-center gap-2 transition-colors"
            data-testid="edit-plan-save-btn"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {saving ? 'Saving…' : 'Save & notify'}
          </button>
        </div>
      </div>
    </div>
  );
};


const StatCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div
    className={`rounded-xl border p-4 ${
      accent ? 'border-[#FFD108]/30 bg-[#FFD108]/[0.04]' : 'border-white/5 bg-[#121212]'
    }`}
    data-testid={testid}
  >
    <div className="flex items-center justify-between mb-2">
      <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">{label}</p>
      <Icon className={`w-4 h-4 ${accent ? 'text-[#FFD108]' : 'text-muted-foreground'}`} />
    </div>
    <p className="text-2xl font-bold tabular-nums">{value}</p>
  </div>
);

const PlanBadge = ({ plan }) => {
  if (plan === 'free') {
    return (
      <span className="text-[10px] font-bold uppercase tracking-wider bg-[#FFD108]/15 text-[#FFD108] px-2 py-0.5 rounded">
        Free
      </span>
    );
  }
  return (
    <span className="text-[10px] font-bold uppercase tracking-wider bg-green-500/15 text-green-400 px-2 py-0.5 rounded">
      Professional
    </span>
  );
};

// 5-dot activation visualiser. Hover shows per-milestone status; the score
// chip uses traffic-light colours so at-risk tenants are spotted in one
// glance during a sales standup.
const MILESTONE_ORDER = [
  ['signup',               'Signed up'],
  ['asset_created',        'Added first asset'],
  ['user_invited',         'Invited a teammate'],
  ['inspection_completed', 'Ran first inspection'],
  ['geofence_created',     'Drew first geofence'],
];

const ActivationCell = ({ tenant }) => {
  const score = tenant?.activation_score ?? 0;
  const total = tenant?.activation_total ?? 5;
  const milestones = tenant?.activation_milestones || {};
  // Sub-3 tenants on a non-free plan are the ones to call this week.
  let toneClass = 'bg-red-500/15 text-red-300 border border-red-500/30';
  if (score >= 4) toneClass = 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30';
  else if (score >= 3) toneClass = 'bg-amber-500/15 text-amber-300 border border-amber-500/30';
  const tooltip = MILESTONE_ORDER
    .map(([k, label]) => `${milestones[k] ? '✓' : '·'} ${label}`)
    .join('\n');
  return (
    <div className="flex items-center gap-2" title={tooltip}>
      <span className={`text-[10px] font-bold tabular-nums px-2 py-0.5 rounded ${toneClass}`}>
        {score}/{total}
      </span>
      <div className="flex items-center gap-1">
        {MILESTONE_ORDER.map(([k]) => (
          <span
            key={k}
            className={`inline-block w-1.5 h-1.5 rounded-full ${milestones[k] ? 'bg-emerald-400' : 'bg-white/15'}`}
            data-testid={`activation-dot-${k}`}
          />
        ))}
      </div>
    </div>
  );
};



const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '—';
  }
};

export default AdminTenantsPage;
